// src/lib/parking.ts — car parks (Task I): site plans, bays drawn on them as % boxes, allocations to tenancies.
// Shapes and calls used by the property page's Parking tab, the tenancy parking card and the resident's
// "Your parking" card. Plan images are stored files (/api/v1/files/:id), fetched with the session token.
import { useEffect, useState } from 'react';
import { api, tokenStore } from './api';

export type BayKind = 'STANDARD' | 'DISABLED' | 'EV' | 'VISITOR' | 'MOTORBIKE' | 'STORAGE';
export type BayStatus = 'FREE' | 'ALLOCATED' | 'RESERVED' | 'OUT_OF_USE';
export const BAY_KINDS: Array<[BayKind, string]> = [['STANDARD', 'Resident bay'], ['DISABLED', 'Disabled'], ['EV', 'EV charging'], ['VISITOR', 'Visitor'], ['MOTORBIKE', 'Motorbike'], ['STORAGE', 'Storage']];
export const BAY_STATUSES: Array<[BayStatus, string]> = [['FREE', 'Free'], ['ALLOCATED', 'Allocated'], ['RESERVED', 'Reserved'], ['OUT_OF_USE', 'Out of use']];

export interface ParkingPlan {
  id: string; propertyId: string; buildingId: string | null; buildingName: string | null; name: string;
  imageUrl: string | null; imageDocumentId: string | null; widthPx: number | null; heightPx: number | null; notes: string | null; bayCount: number; updatedAt: string | null;
}
export interface Allocation {
  id: string; bayId: string; bayCode: string; tenancyId: string; tenancyReference: string; tenancyStatus: string; unitId: string; unitReference: string | null;
  tenants: string; fromDate: string; toDate: string | null; active: boolean; vehicleReg: string | null; vehicle: string | null; permit: string | null;
  notes: string | null; releasedReason: string | null; createdByName: string | null; createdAt: string | null;
}
export interface ParkingBay {
  id: string; planId: string | null; propertyId: string; code: string; kind: BayKind; kindLabel: string;
  x: number | null; y: number | null; w: number | null; h: number | null; rotation: number | null; onPlan: boolean;
  status: BayStatus; statusLabel: string; allocatable: boolean; notes: string | null; allocation: Allocation | null;
}
export interface Availability { total: number; onPlan: number; free: number; allocated: number; reserved: number; outOfUse: number; visitor: number; byKind: Record<BayKind, number> }
export interface PropertyParking {
  property: { id: string; name: string }; plans: ParkingPlan[]; bays: ParkingBay[]; availability: Availability;
  can: { create: boolean; update: boolean; allocate: boolean; release: boolean };
}
export interface TenancyParking {
  tenancyId: string; tenancyReference: string; propertyId: string; bay: ParkingBay | null; allocation: Allocation | null;
  plan: { id: string; name: string; imageUrl: string | null; widthPx: number | null; heightPx: number | null } | null;
  history: Allocation[]; propertyHasBays: boolean;
}
export interface AllocatableTenancy { id: string; reference: string; status: string; startDate: string | null; unitReference: string; tenants: string; bayCode: string | null }
export interface PlanImage { fileName: string; contentType: string; dataBase64: string; widthPx: number; heightPx: number }
export interface BayPatch { code?: string; kind?: BayKind; status?: BayStatus; notes?: string | null; planId?: string | null; x?: number | null; y?: number | null; w?: number | null; h?: number | null }
export interface GridInput {
  propertyId: string; planId?: string | null; rows: number; cols: number; codePattern?: string; startNumber?: number; kind?: BayKind;
  originX?: number; originY?: number; bayW?: number; bayH?: number; gapX?: number; gapY?: number;
}

export const parkingApi = {
  property: async (propertyId: string) => (await api.get<PropertyParking>(`/properties/${propertyId}/parking`)).data,
  tenancies: async (propertyId: string, q?: string) => (await api.get<AllocatableTenancy[]>(`/properties/${propertyId}/parking/tenancies`, { q })).data,
  bays: async (q: { propertyId?: string; status?: BayStatus; kind?: BayKind; q?: string }) => (await api.get<ParkingBay[]>('/parking/bays', q)).data,
  createPlan: async (body: { propertyId: string; name: string; buildingId?: string | null; notes?: string | null; image?: PlanImage }) => (await api.post<ParkingPlan>('/parking/plans', body)).data,
  updatePlan: async (id: string, body: { name?: string; notes?: string | null; image?: PlanImage | null }) => (await api.patch<ParkingPlan>(`/parking/plans/${id}`, body)).data,
  deletePlan: async (id: string) => api.delete(`/parking/plans/${id}`),
  createBay: async (body: BayPatch & { propertyId: string; code: string }) => (await api.post<ParkingBay>('/parking/bays', body)).data,
  createGrid: async (body: GridInput) => (await api.post<ParkingBay[]>('/parking/bays/bulk', body)).data,
  updateBay: async (id: string, body: BayPatch) => (await api.patch<ParkingBay>(`/parking/bays/${id}`, body)).data,
  deleteBay: async (id: string) => api.delete(`/parking/bays/${id}`),
  allocate: async (bayId: string, body: { tenancyId: string; fromDate?: string; vehicleReg?: string | null; vehicle?: string | null; permit?: string | null }) => (await api.post<Allocation>(`/parking/bays/${bayId}/allocate`, body)).data,
  release: async (bayId: string, body: { toDate?: string } = {}) => (await api.post<ParkingBay>(`/parking/bays/${bayId}/release`, body)).data,
  updateAllocation: async (id: string, body: { vehicleReg?: string | null; vehicle?: string | null; permit?: string | null; notes?: string | null }) => (await api.patch<Allocation>(`/parking/allocations/${id}`, body)).data,
  forTenancy: async (tenancyId: string) => (await api.get<TenancyParking>(`/tenancies/${tenancyId}/parking`)).data,
  mine: async () => (await api.get<TenancyParking[]>('/parking/mine')).data,
};

/** Bay colours by status (and visitor bays, which are shared). */
export function bayTone(b: Pick<ParkingBay, 'status' | 'kind'>): { fill: string; border: string; text: string; label: string } {
  if (b.kind === 'VISITOR') return { fill: 'rgba(251,191,36,0.55)', border: '#d97706', text: '#78350f', label: 'Visitor' };
  switch (b.status) {
    case 'ALLOCATED': return { fill: 'rgba(176,122,74,0.78)', border: '#7c4f28', text: '#ffffff', label: 'Allocated' };
    case 'RESERVED': return { fill: 'rgba(167,139,250,0.6)', border: '#7c3aed', text: '#3b0764', label: 'Reserved' };
    case 'OUT_OF_USE': return { fill: 'rgba(100,116,139,0.55)', border: '#334155', text: '#0f172a', label: 'Out of use' };
    default: return { fill: 'rgba(16,185,129,0.35)', border: '#047857', text: '#064e3b', label: 'Free' };
  }
}

/**
 * Read an image file and shrink it so its longest side is at most `max` px (prototype: the floorplan is
 * downscaled before it is stored). Returns a JPEG (PNG kept as PNG for line drawings) with its pixel size.
 */
export function downscaleImage(file: File, max = 1600): Promise<PlanImage> {
  return new Promise((resolve, reject) => {
    if (!file.type.startsWith('image/')) { reject(new Error('Choose an image (PNG or JPG) of the car park plan')); return; }
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, max / Math.max(img.naturalWidth, img.naturalHeight));
      const w = Math.max(1, Math.round(img.naturalWidth * scale)); const h = Math.max(1, Math.round(img.naturalHeight * scale));
      const canvas = document.createElement('canvas'); canvas.width = w; canvas.height = h;
      const g = canvas.getContext('2d');
      if (!g) { URL.revokeObjectURL(url); reject(new Error('This browser cannot resize images')); return; }
      g.fillStyle = '#ffffff'; g.fillRect(0, 0, w, h);
      g.drawImage(img, 0, 0, w, h);
      URL.revokeObjectURL(url);
      const png = file.type === 'image/png';
      const dataUrl = canvas.toDataURL(png ? 'image/png' : 'image/jpeg', 0.85);
      const base = file.name.replace(/\.[^.]+$/, '') || 'plan';
      resolve({ fileName: `${base}.${png ? 'png' : 'jpg'}`, contentType: png ? 'image/png' : 'image/jpeg', dataBase64: dataUrl.replace(/^data:[^;]*;base64,/, ''), widthPx: w, heightPx: h });
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('That file could not be read as an image')); };
    img.src = url;
  });
}

/** A stored file as an object URL, fetched with the session token (never a public link). */
export function useStoredImage(url: string | null | undefined): { src: string | null; error: boolean } {
  const [state, setState] = useState<{ src: string | null; error: boolean }>({ src: null, error: false });
  useEffect(() => {
    if (!url) { setState({ src: null, error: false }); return; }
    let href: string | null = null; let live = true;
    void (async () => {
      try {
        const res = await fetch(url, { headers: { authorization: `Bearer ${tokenStore.get()?.accessToken ?? ''}` } });
        if (!res.ok) throw new Error(String(res.status));
        href = URL.createObjectURL(await res.blob());
        if (live) setState({ src: href, error: false }); else URL.revokeObjectURL(href);
      } catch { if (live) setState({ src: null, error: true }); }
    })();
    return () => { live = false; if (href) URL.revokeObjectURL(href); };
  }, [url]);
  return state;
}

export const isPhone = () => typeof window !== 'undefined' && window.matchMedia('(max-width: 640px)').matches;
